import React, { ReactNode } from 'react';
import type { UseQueryObjectProps, UseQuerySqlProps, WorkflowType } from '@fabric-ui/components';
import { SxProps } from '@mui/material';
import { RequestDocument, Variables } from 'graphql-request';

export enum SortType {
  ASC = 'asc',
  DESC = 'desc',
}
export enum GroupHeaderRowType {
  GROUP = 'GROUP',
  SUB_GROUP = 'SUB_GROUP',
  STANDALONE = 'STANDALONE',
}

export interface HeadCellType {
  id: string;
  key?: string;
  label?: React.ReactNode;
  filter?: any;
  sx?: any;
  type?: FormatterType;
  fieldName?: string;
  initWidth?: number;
  disableSortBy?: boolean;
  sort?: SortType;
  formatter?: (data, row, setRows, index, rows, id) => ReactNode;
  groupHeaderType?: GroupHeaderRowType;
  isKey?: boolean;
  hidden?: boolean;
  initSort?: SortType;
  style?: any;
  editable?: boolean;
  options?: any;
  getValue?: (row, colId?, options?) => any;
  getFilterOptionFunc?: (row) => any;
  hasTooltip?: boolean;
  rowStyle?: any;
  linkProps?: LinkPropsType;
  disableFilter?: boolean;
  isHighlighted?: boolean;
  asyncGetOptions?: AsyncGetOptions;
  getOptions?: (data: any, label: ReactNode, v) => any[];
}
export type LinkPropsType = {
  shouldShowLink: (row) => boolean;
  onClickLink?: (row) => void;
  tooltip?: string;
};

export interface PaginationProps {
  onPageChange: (pageNum: number) => void;
  totalRows?: number;
  pageNum: number;
}

interface TotalCell {
  formatter?: (data) => any;
  id: string;
  type?: string;
  options?;
  sx?: SxProps;
}

export interface ColumnIconButtonProp {
  onClick: (colId?) => void;
  icon: React.ReactNode;
  disableFirstColumn?: boolean;
}

interface NoDataProps {
  headline?: string;
}

export interface GroupedHeadCell {
  cells: any[] | EnhancedHeadCell[];
  innerCols: number;
  isMultiLevel?: boolean;
  emptyCellStyle?: SxProps;
}

export type EnhancedHeadCell = {
  value: string | number | null | undefined;
  label: string;
  sx: SxProps;
  cells: EnhancedHeadCell[];
  innerCols: number;
  isChild?: boolean;
};

export interface AvTableProps {
  storageKey?: string;
  resizable?: boolean;
  groupedHeadCells?: GroupedHeadCell;
  headCells?: HeadCellType[];
  rows?: Record<string, unknown>[];
  onSelectAllRows?: (b: boolean) => void;
  hasHeaderFilters?: boolean;
  setRows?: (rows: any[]) => void;
  selectedKeys?: (number | string)[];
  activeKey?: number | string;
  isSelectionControlled?: boolean;
  onSelected?: (newVal, rows) => void;
  onActive?: (newVal) => void;
  onScroll?: (e, containerRef) => void;
  allowKeyboardNavigation?: boolean;
  deleteProps?: DeleteProps;
  loading?: boolean;
  multiSelect?: boolean;
  allowEdit?: boolean | ((row) => boolean);
  allowCreate?: boolean;
  showHeader?: boolean;
  pagination?: boolean;
  onEdit?: (row, field, value) => void;
  onSort?: (property: string, direction: SortType | null, event, options) => void;
  customActions?: ((selected, selectedRows, setRows, handleSelected) => ReactNode)[];
  inlineActions?: ((row, setRows?: () => void, rows?: any[], handleSelected?: () => void, index?: number) => ReactNode)[];
  customGlobalActions?: (() => ReactNode)[];
  markedCells?: Record<string, any>;
  expandedRowObj?: {
    component: (...args: any[]) => any;
    shouldExpand?: (row) => boolean;
  };
  innerSelectLink?: (id, row) => string | { to: string; search?: any; state?: any };
  innerEditLink?: (id) => string | { to: string; search?: any; state?: any };
  enableSelectionColor?: boolean;
  rowLimit?: number;
  size?: 'large' | 'small' | 'xSmall' | 'tiles' | 'medium';
  sx?: Record<string, any>;
  tableContainerSx?: Record<string, any>;
  rowGroupByField?: (row) => any;
  loadMoreLimit?: number;
  totalProps?: TotalsProps;
  paginationProps?: PaginationProps;
  columnIconButtonProp?: ColumnIconButtonProp;
  onCancelColumn?: (v) => void;
  customNewButton?: React.ReactNode;
  noDataProps?: NoDataProps;
  disableUseEffect?: boolean;
  shouldDisplayNull?: boolean;
  displayFreeSpaceCell?: boolean;
  getSortingDir?: ({
    headCells,
    selectedId,
    selectedOptions,
  }: {
    headCells: HeadCellType[];
    selectedId: string;
    selectedOptions?: { group: string };
  }) => SortType | undefined;
  disableRowClickNavigation?: (row) => boolean;
  disableSelectAll?: boolean;
  onFilterChange?: (filters: Record<string, any>) => void;
  disableInternalFilter?: boolean;
}

export interface TotalsProps {
  row?: any;
  label?: string;
  colspan?: number;
  cells?: TotalCell[];
  sx?: SxProps;
}

export interface DeleteProps {
  uri?: string;
  query?: string;
  workflowType?: WorkflowType;
  onDeleteSuccess?: (args?: any) => void;
  onDelete?: (v) => void;
  getName?: (v) => string;
  message?: ReactNode;
  isConfirmDeletion?: boolean;
  title?: string;
  customMessage?: (v: (Record<string, any> | undefined)[]) => ReactNode;
  disableMultipleDelete?: boolean;
  workflowReturnsStatusOnly?: boolean;
  isDisabled?: (v) => boolean;
  disabledTooltip?: (v) => string;
}

export enum FormatterType {
  boolean = 'bool',
  number = 'number',
  percentage = 'percentage',
  historicDate = 'historic-date',
  string = 'string',
  date = 'date',
  array = 'array',
  json = 'json',
  loading = 'loading',
}

type GqlAsyncOptions = {
  type: OptionQueryType;
  query: RequestDocument;
  variables?: Variables;
  select: (data: any) => { title: string; value: any }[];
};

type GqlPaginationAsyncOptions = {
  type: OptionQueryType;
  query: RequestDocument;
  variables?: Variables;
  select: (data: any) => { data: { title: string; value: any }[]; totals: number };
};

type SqlAsyncOptions = {
  type: OptionQueryType;
} & UseQuerySqlProps;

type QueryObjectAsyncOptions = {
  type: OptionQueryType;
} & UseQueryObjectProps;

export type AsyncGetOptions = (search: string) => GqlAsyncOptions | SqlAsyncOptions | QueryObjectAsyncOptions | GqlPaginationAsyncOptions;

export enum OptionQueryType {
  GQL = 'gql',
  SQL = 'sql',
  Object = 'queryObject',
  GQL_PAGINATION = 'gqlPagination',
}
export type OltpPagination<T> = {
  content: T;
  totalElements: number;
  totalPages: number;
  last: boolean;
  number: number;
};
